// import { useOktaAuth } from "@okta/okta-react";
import { useKeycloak } from "@react-keycloak/web"
import { AppOverlay } from "components/common/overlays"
import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { userLogin } from "store/slices/user"
import { useExtension } from "utils"

const AuthState = {
  unauthenticated: "unauthenticated",
  kcloakAuthenticatedOnly: "kcloakAuthenticatedOnly",
  kcloakAuthenticatedAndAppAuthenticating:
    "kcloakAuthenticatedAndAppAuthenticating",
  authenticated: "authenticated",
  loggingOut: "loggingOut",
  authenticationFailed: "authenticationFailed"
}

function computeCurrentAuthState(
  authStates,
  incrementAuthStateAttemptCount,
  resetAuthStateAttemptCount,
  dispatchUserLogin
) {
  if (authStates.authAttemptCount > 5) {
    return AuthState.authenticationFailed
  } else if (authStates.appAuthStateIsLoggingOut) {
    return AuthState.loggingOut
  } else if (authStates.kcloakIsAuthenticated) {
    if (authStates.appIsAuthenticated) {
      resetAuthStateAttemptCount()
      return AuthState.authenticated
    } else if (authStates.appAuthStateIsLoggingIn) {
      return AuthState.kcloakAuthenticatedAndAppAuthenticating
    } else {
      incrementAuthStateAttemptCount()
      dispatchUserLogin()
      return AuthState.kcloakAuthenticatedAndAppAuthenticating
    }
  } else {
    return AuthState.unauthenticated
  }
}
function useMonitorAuthState() {
  const dispatch = useDispatch()
  const extension = useExtension()
  const [authState, setAuthState] = React.useState(AuthState.unauthenticated)
  const [authAttemptCount, setAuthAttemptCount] = React.useState(0)

  const { keycloak } = useKeycloak()

  const appIsAuthenticated = useSelector((state) => state.user.isAuthenticated)
  const kcloakIsAuthenticated = keycloak.authenticated

  const appAuthStateIsLoggingIn = useSelector((state) => state.user.isLoggingIn)
  const appAuthStateIsLoggingOut = useSelector(
    (state) => state.user.isLoggingOut
  )

  const incrementAuthAttemptCount = React.useCallback(() => {
    setAuthAttemptCount((authStateAttemptCount) => authStateAttemptCount + 1)
  }, [setAuthAttemptCount])
  const resetAuthAttemptCount = React.useCallback(
    () => setAuthAttemptCount(0),
    [setAuthAttemptCount]
  )
  const dispatchUserLogin = React.useCallback(() => {
    dispatch(userLogin({ auth: keycloak, extension }))
  }, [dispatch, extension, keycloak])
  React.useEffect(() => {
    const computedState = computeCurrentAuthState(
      {
        appIsAuthenticated,
        kcloakIsAuthenticated,
        appAuthStateIsLoggingIn,
        appAuthStateIsLoggingOut,
        authAttemptCount
      },
      incrementAuthAttemptCount,
      resetAuthAttemptCount,
      dispatchUserLogin
    )
    setAuthState(computedState)
  }, [
    authAttemptCount,
    appIsAuthenticated,
    kcloakIsAuthenticated,
    appAuthStateIsLoggingIn,
    appAuthStateIsLoggingOut,
    incrementAuthAttemptCount,
    resetAuthAttemptCount,
    dispatchUserLogin
  ])

  return authState
}
export default function AuthMonitor() {
  const monitorAuthState = useMonitorAuthState()

  if (monitorAuthState === AuthState.kcloakAuthenticatedAndAppAuthenticating) {
    console.log("Authenticating...")
    return <AppOverlay messageTitle="Authenticating..." messageBody="" blink />
  }
  if (monitorAuthState === AuthState.loggingOut) {
    console.log("Logging Out...")
    return <AppOverlay messageTitle="Logging Out..." messageBody="" blink />
  }
  if (monitorAuthState === AuthState.authenticationFailed) {
    console.log("Authentication Failed.")
    return (
      <AppOverlay
        messageTitle="Authentication Failed."
        messageBody="Unable to complete authentication process. Please contact support."
        overlayColor="rgba(255,220,240)"
        danger
      />
    )
  }
  return null
}
